import { useEffect, useState } from 'react';
import { ContactFields, isAuthError, useAuth0 } from '@brighte/passwordless-authenticator';
import { ApplicantType } from '@brighte/validation';
import { Loader } from '@components/Loader/Loader';
import PageTitle from '@components/PageTitle/PageTitle';
import { datadogRum } from '@datadog/browser-rum';
import {
  PropertyTitleOwnerOutput,
  PropertyTitleSearchAddressInput,
  useGetPropertyTitleOwnerQuery,
  useHandlePostApplicationLoginMutation,
  useReferralQuery,
  useSubmitTitleOwnerIncomeMutation,
} from '@graphql/index';
import { useApplicationData, useSetApplicationData } from '@provider';
import { getApplicantTypeAndTrackingIdFromUrl, isJointApplicant } from '@utils';
import ConfirmInstallationAddress from './ConfirmInstallationAddress';
import DeclareTaxableIncome from './DeclareTaxableIncome';
import PropertyTitleCheck from './PropertyTitleCheck';
import TitleOwnerResults from './TitleOwnerResults';
import TitleOwnerTerms from './TitleOwnerTerms';
import VerifyDeclaredIncome from './VerifyDeclaredIncome';

type LandTitleStep = 'terms' | 'address' | 'titleCheck' | 'titleOwnerResults' | 'declareIncome' | 'verifyIncome';

type LandTitleEligibilityCheckProps = {
  onComplete: () => void;
};

function LandTitleEligibilityCheck({ onComplete }: LandTitleEligibilityCheckProps) {
  const [step, setStep] = useState<LandTitleStep>('terms');
  const [titleOwners, setTitleOwners] = useState<PropertyTitleOwnerOutput[]>([]);
  const [declaredIncomes, setDeclaredIncomes] = useState<Record<string, string>>({});
  const prePopContactDetails = useApplicationData('ContactDetails');
  const { quote } = useApplicationData(['quote']);
  const setApplicationData = useSetApplicationData();
  const { verificationCompleted } = useAuth0();
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const joint = isJointApplicant();
  const { mutateAsync: submitIncome } = useSubmitTitleOwnerIncomeMutation({ useErrorBoundary: false });
  const { mutate: handlePostApplicationLogin } = useHandlePostApplicationLoginMutation();

  const { data: referral, isLoading } = useReferralQuery(
    { id: trackingId, joint },
    {
      enabled: verificationCompleted,
      select: data => data.referral,
      retry: 0,
      useErrorBoundary: (error: unknown) => isAuthError(error),
    },
  );

  const { data: existingTitleData, isLoading: isExistingOwnersLoading } = useGetPropertyTitleOwnerQuery(
    { input: { trackingId } },
    {
      enabled: verificationCompleted,
      select: data => data.getPropertyTitleOwner,
      retry: 0,
      useErrorBoundary: (error: unknown) => isAuthError(error),
    },
  );

  const existingOwners = existingTitleData?.owners;

  const isIncomeVerificationAlreadyComplete =
    existingTitleData?.ownershipDisputed === true ||
    (!!existingOwners?.length &&
      existingOwners.every(owner => {
        const normallyVerified =
          owner.aiIncomeCalculated != null && owner.declaredIncome != null && (owner.documents?.length ?? 0) > 0;
        // Covers $0 income and failed_final bypass: submit was called with
        // isIncomeVerified: false explicitly. isIncomeVerified is null until
        // submitTitleOwnerIncome runs, so false + declaredIncome != null is a
        // reliable signal the owner completed the flow via a bypass path.
        const bypassSubmitted = owner.isIncomeVerified === false && owner.declaredIncome != null;
        return normallyVerified || bypassSubmitted;
      }));

  useEffect(() => {
    if (isIncomeVerificationAlreadyComplete && step === 'terms') {
      onComplete();
    }
  }, [isIncomeVerificationAlreadyComplete, step]);

  const handleLoginError = (error: unknown) => {
    if (isAuthError(error)) {
      throw error;
    }
  };

  const handleVerificationSuccess = ({
    verifiedEmail,
    verifiedMobile,
  }: {
    verifiedEmail?: string;
    verifiedMobile: string;
  }) => {
    setApplicationData({ ContactDetails: { email: verifiedEmail, mobile: verifiedMobile } });
    handlePostApplicationLogin({
      postLoginInput: {
        applicantType: joint ? ApplicantType.Joint : ApplicantType.Primary,
        opportunityId: quote?.opportunity?.id,
        trackingId,
      },
    });
  };

  if (!verificationCompleted) {
    return (
      <>
        <PageTitle />
        <ContactFields
          verificationDetails={{
            email: prePopContactDetails?.email,
            mobile: prePopContactDetails?.mobile,
            verificationType: prePopContactDetails?.verificationType,
            maskedEmail: prePopContactDetails?.maskedEmail,
            maskedMobile: prePopContactDetails?.maskedMobile,
          }}
          onError={handleLoginError}
          onSuccess={handleVerificationSuccess}
        />
      </>
    );
  }

  if (isLoading || isExistingOwnersLoading || isIncomeVerificationAlreadyComplete) {
    return <Loader />;
  }

  const addr = referral?.address;
  const productName = referral?.financialProduct?.name ?? '';
  const vendorName = referral?.vendor?.tradingName ?? '';
  const applicantName = [referral?.customer?.firstName, referral?.customer?.lastName].filter(Boolean).join(' ');
  const formattedAddress = addr
    ? [
        addr.unitNumber
          ? `${addr.unitNumber}/${addr.streetNumber} ${addr.streetName}`
          : `${addr.streetNumber} ${addr.streetName}`,
        `${addr.suburb} ${addr.state} ${addr.postcode}`,
      ].join(', ')
    : '';

  const rawAddress: PropertyTitleSearchAddressInput | null = addr
    ? {
        unitNumber: addr.unitNumber || undefined,
        streetNumber: addr.streetNumber,
        streetName: addr.streetName,
        suburb: addr.suburb,
        state: addr.state,
        postcode: addr.postcode,
      }
    : null;

  if (step === 'verifyIncome') {
    return (
      <VerifyDeclaredIncome
        owners={titleOwners}
        declaredIncomes={declaredIncomes}
        trackingId={trackingId}
        productName={productName}
        applicantName={applicantName}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Income Verified', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          onComplete();
        }}
        onBack={() => setStep('declareIncome')}
      />
    );
  }

  if (step === 'titleOwnerResults') {
    const handleTitleDisputed = async () => {
      datadogRum.addAction('LandOwner Title Disputed', { trackingId });
      try {
        await submitIncome({ input: { applicationId: trackingId, ownershipDisputed: true, owners: [] } });
      } catch {
        datadogRum.addAction('LandOwner Title Disputed Submit Error', { trackingId });
      }
      onComplete();
    };

    return (
      <TitleOwnerResults
        owners={titleOwners}
        applicantName={applicantName}
        productName={productName}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Title Confirmed', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          setStep('declareIncome');
        }}
        onTitleDisputed={handleTitleDisputed}
        onSelectionChange={selection => {
          datadogRum.addAction(
            selection === 'yes' ? 'LandOwner Title Owners Confirmed' : 'LandOwner Title Owners Disputed',
            { trackingId },
          );
        }}
      />
    );
  }

  if (step === 'declareIncome') {
    return (
      <DeclareTaxableIncome
        owners={titleOwners}
        productName={productName}
        initialIncomes={declaredIncomes}
        onConfirm={incomes => {
          datadogRum.addAction('LandOwner Income Declared', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          setDeclaredIncomes(incomes);
          setStep('verifyIncome');
        }}
        onBack={() => setStep('titleOwnerResults')}
      />
    );
  }

  if (step === 'titleCheck' && rawAddress) {
    return (
      <PropertyTitleCheck
        address={formattedAddress}
        rawAddress={rawAddress}
        trackingId={trackingId}
        productName={productName}
        onContinue={owners => {
          datadogRum.addAction('LandOwner Title Check Complete', {
            trackingId,
            ownerCount: owners.length,
          });
          setTitleOwners(owners);
          setStep('titleOwnerResults');
        }}
        onContinueToApplication={() => {
          datadogRum.addAction('LandOwner Title Check Unavailable Continue', { trackingId });
          onComplete();
        }}
        onBack={() => setStep('address')}
      />
    );
  }

  if (step === 'address') {
    return (
      <ConfirmInstallationAddress
        address={formattedAddress}
        productName={productName}
        vendorName={vendorName}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Address Confirmed', { trackingId });
          setStep('titleCheck');
        }}
        onBack={() => setStep('terms')}
      />
    );
  }

  return (
    <TitleOwnerTerms
      productName={productName}
      onContinue={() => {
        datadogRum.addAction('LandOwner Terms Accepted', { trackingId });
        setStep('address');
      }}
    />
  );
}

export default LandTitleEligibilityCheck;
